import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faArrowUpRight, faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { ROUTE_CONFIG } from "@/routes/config";

import { SettingsCard } from "./SettingsCard";

interface AccountStatusCardProps {
	/** Mirrors `user.linkedinAllowed` — true for sending users. */
	isSendingUser: boolean;
}

export const AccountStatusCard: FunctionComponent<AccountStatusCardProps> = ({ isSendingUser }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.accountStatus.${key}`);
	const navigate = useNavigate();

	return (
		<SettingsCard icon={faCircleCheck} subtitle={ts("subtitle")} title={ts("title")}>
			<div className="flex items-center gap-4 px-5 py-4">
				<div className="min-w-0 flex-1">
					<div className="text-sm font-semibold text-text">{ts("sendingStatus")}</div>
					<div className="mt-0.5 text-[12.5px] text-text-meta">
						{isSendingUser ? ts("sendingDescription") : ts("nonSendingDescription")}
					</div>
				</div>

				<div className="flex flex-shrink-0 items-center gap-3">
					<span
						className={classNames(
							"inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold",
							isSendingUser ? "bg-green-200 text-green-700" : "bg-gray-100 text-text-meta"
						)}
					>
						<span
							className={classNames(
								"h-1.5 w-1.5 rounded-full",
								isSendingUser ? "bg-green-700" : "bg-text-meta"
							)}
						/>
						{isSendingUser ? ts("sendingUser") : ts("nonSendingUser")}
					</span>

					<Button
						color={ButtonColor.ACTION_SECONDARY}
						iconRight
						image={<Icon className="ml-1 h-3.5 w-3.5" icon={faArrowUpRight} />}
						size={ButtonSize.S}
						title={ts("manageInCompanySettings")}
						type="button"
						onClick={() => navigate(`${ROUTE_CONFIG.COMPANY}?tab=USER`)}
					/>
				</div>
			</div>
		</SettingsCard>
	);
};
